import { useState, useEffect } from "react";
import { Link, Routes, Route, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, FileText, Calendar, BarChart3, UserCheck, 
  ArrowLeft, Plus, Trash2, Shield, Heart,
  Sparkles, Baby, Star, CheckCircle, XCircle,
  Image, Type, RefreshCw, Save, Eye, Settings,
  GripVertical, Edit2, ToggleLeft, ToggleRight,
  Video, Link as LinkIcon, Copy, ExternalLink
} from "lucide-react";

// Admin Sidebar
const AdminSidebar = () => {
  const location = useLocation();
  const { user } = useAuth();
  
  const menuItems = [
    { path: "/admin", icon: BarChart3, label: "Overview", exact: true },
    { path: "/admin/site-content", icon: Image, label: "Landing Page CMS" },
    { path: "/admin/dashboard-cms", icon: FileText, label: "Dashboard CMS" },
    { path: "/admin/app-settings", icon: Settings, label: "App Settings" },
    { path: "/admin/calendar-settings", icon: Video, label: "Google Meet" },
    { path: "/admin/users", icon: Users, label: "Users" },
    { path: "/admin/experts", icon: UserCheck, label: "Experts" },
    { path: "/admin/bookings", icon: Calendar, label: "Session Bookings" },
    { path: "/admin/events", icon: Calendar, label: "Events" },
    { path: "/admin/circles", icon: Heart, label: "Circles" },
    { path: "/admin/data-export", icon: FileText, label: "Data Export" },
  ];

  return (
    <aside className="w-64 bg-white border-r border-sage/20 min-h-screen p-6 fixed left-0 top-0">
      <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-sage-dark mb-8">
        <ArrowLeft className="w-4 h-4" />
        Back to App
      </Link>
      
      <div className="flex items-center gap-3 mb-8">
        <Avatar className="h-10 w-10">
          <AvatarImage src={user?.picture} />
          <AvatarFallback className="bg-sage/20 text-sage-dark">{user?.name?.[0]}</AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sage-darker font-medium">{user?.name}</p>
          <Badge className="bg-purple-100 text-purple-600 text-xs">
            <Shield className="w-3 h-3 mr-1" />
            Admin
          </Badge>
        </div>
      </div>

      <nav className="space-y-2">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.exact 
            ? location.pathname === item.path 
            : location.pathname.startsWith(item.path) && item.path !== "/admin";
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                isActive 
                  ? 'bg-sage/20 text-sage-dark' 
                  : 'text-muted-foreground hover:text-sage-darker hover:bg-cream'
              }`}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <Icon className="w-5 h-5" />
              {item.label}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
};

// Admin Overview
const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Stats error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-24 bg-sage/10 rounded-xl" />)}
      </div>
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <h1 className="text-2xl font-serif text-sage-darker">Dashboard Overview</h1>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border-sage/20">
          <CardContent className="p-5">
            <Users className="w-8 h-8 text-sage mb-2" />
            <p className="text-2xl font-bold text-sage-darker">{stats?.total_users || 0}</p>
            <p className="text-sm text-muted-foreground">Total Users</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-sage/20">
          <CardContent className="p-5">
            <Heart className="w-8 h-8 text-clay mb-2" />
            <p className="text-2xl font-bold text-sage-darker">{stats?.total_connections || 0}</p>
            <p className="text-sm text-muted-foreground">Connections</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-sage/20">
          <CardContent className="p-5">
            <FileText className="w-8 h-8 text-purple-500 mb-2" />
            <p className="text-2xl font-bold text-sage-darker">{stats?.total_content || 0}</p>
            <p className="text-sm text-muted-foreground">Content Items</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-sage/20">
          <CardContent className="p-5">
            <Calendar className="w-8 h-8 text-blue-500 mb-2" />
            <p className="text-2xl font-bold text-sage-darker">{stats?.total_events || 0}</p>
            <p className="text-sm text-muted-foreground">Events</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-sage/20">
        <CardHeader>
          <CardTitle className="text-lg font-serif text-sage-darker">Users by Phase</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-clay/10 text-center">
              <Sparkles className="w-6 h-6 text-clay-dark mx-auto mb-2" />
              <p className="text-xl font-bold text-sage-darker">{stats?.users_by_phase?.ttc || 0}</p>
              <p className="text-xs text-muted-foreground">Trying to Conceive</p>
            </div>
            <div className="p-4 rounded-xl bg-sage/10 text-center">
              <Heart className="w-6 h-6 text-sage-dark mx-auto mb-2" />
              <p className="text-xl font-bold text-sage-darker">{stats?.users_by_phase?.pregnant || 0}</p>
              <p className="text-xs text-muted-foreground">Pregnant</p>
            </div>
            <div className="p-4 rounded-xl bg-purple-100 text-center">
              <Baby className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-xl font-bold text-sage-darker">{stats?.users_by_phase?.postpartum || 0}</p>
              <p className="text-xs text-muted-foreground">Postpartum</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Calendar/Google Meet Settings
const AdminCalendarSettings = () => {
  const [status, setStatus] = useState({ configured: false, message: "" });
  const [loading, setLoading] = useState(true);
  const [authUrl, setAuthUrl] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/calendar/status`, { withCredentials: true });
      setStatus(response.data);
    } catch (error) {
      toast.error("Failed to check calendar status");
    } finally {
      setLoading(false);
    }
  };

  const getAuthUrl = async () => {
    try {
      const response = await axios.get(`${API}/admin/calendar/auth-url`, { withCredentials: true });
      setAuthUrl(response.data.auth_url);
      toast.success("Auth URL generated! Follow the instructions below.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate auth URL");
    }
  };

  const completeAuth = async () => {
    if (!authCode.trim()) {
      toast.error("Please enter the authorization code");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/calendar/complete-auth`, { code: authCode.trim() }, { withCredentials: true });
      toast.success("Google Calendar connected successfully!");
      setAuthUrl("");
      setAuthCode("");
      checkStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete authentication");
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-sage-darker mb-2">Google Meet Integration</h1>
        <p className="text-muted-foreground">
          Connect Google Calendar to automatically generate Google Meet links for expert sessions and events.
        </p>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-sage" />
            Connection Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            {status.configured ? (
              <>
                <CheckCircle className="w-6 h-6 text-green-500" />
                <div>
                  <p className="font-medium text-green-600">Connected</p>
                  <p className="text-sm text-muted-foreground">{status.message}</p>
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-6 h-6 text-amber-500" />
                <div>
                  <p className="font-medium text-amber-600">Not Connected</p>
                  <p className="text-sm text-muted-foreground">{status.message}</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Setup Card - show if not configured */}
      {!status.configured && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-sage" />
              Connect Google Calendar
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {!authUrl ? (
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Click the button below to start the Google Calendar authorization process.
                </p>
                <Button onClick={getAuthUrl} className="bg-sage hover:bg-sage-dark text-white">
                  Generate Authorization URL
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">Instructions:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-blue-700">
                    <li>Click the button below to open Google authorization in a new tab</li>
                    <li>Sign in with your Google account (wombscircle@gmail.com)</li>
                    <li>Grant calendar access permissions</li>
                    <li>Copy the authorization code shown</li>
                    <li>Paste the code below and click &quot;Complete Setup&quot;</li>
                  </ol>
                </div>

                <div className="space-y-3">
                  <Label>Authorization URL</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={authUrl} 
                      readOnly 
                      className="font-mono text-xs bg-gray-50"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => copyToClipboard(authUrl)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => window.open(authUrl, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="authCode">Authorization Code</Label>
                  <Input
                    id="authCode"
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value)}
                    placeholder="Paste the authorization code here"
                  />
                </div>

                <Button 
                  onClick={completeAuth} 
                  disabled={submitting || !authCode}
                  className="bg-sage hover:bg-sage-dark text-white"
                >
                  {submitting ? "Connecting..." : "Complete Setup"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Features Info */}
      <Card>
        <CardHeader>
          <CardTitle>What This Enables</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-sage/10 rounded-lg">
              <h4 className="font-medium text-sage-darker mb-2">Expert Sessions</h4>
              <p className="text-sm text-muted-foreground">
                When a user books an expert session, a Google Meet link is automatically generated 
                and included in their confirmation email.
              </p>
            </div>
            <div className="p-4 bg-sage/10 rounded-lg">
              <h4 className="font-medium text-sage-darker mb-2">Online Events</h4>
              <p className="text-sm text-muted-foreground">
                Admins can generate Google Meet links for online events. The link is shown to 
                registered users in their confirmation email and on their profile.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Users
const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [interestFilter, setInterestFilter] = useState("all");
  const [adminFilter, setAdminFilter] = useState("all");
  const [allInterests, setAllInterests] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
      
      // Extract all unique interests
      const interests = new Set();
      response.data.forEach(u => {
        if (u.interests && Array.isArray(u.interests)) {
          u.interests.forEach(i => interests.add(i));
        }
      });
      setAllInterests(Array.from(interests).sort());
    } catch (error) {
      console.error("Users error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-admin`, {}, { withCredentials: true });
      toast.success("Admin status updated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update admin status");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const getPhaseLabel = (phase) => {
    switch (phase) {
      case 'ttc': return 'TTC';
      case 'pregnant': return 'Pregnant';
      case 'postpartum': return 'Postpartum';
      default: return 'N/A';
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    // Search filter (name or email)
    if (searchTerm && !u.name?.toLowerCase().includes(searchTerm.toLowerCase()) && !u.email?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    // Phase filter
    if (phaseFilter !== "all" && u.phase !== phaseFilter) {
      return false;
    }
    // Interest filter
    if (interestFilter !== "all" && (!u.interests || !u.interests.includes(interestFilter))) {
      return false;
    }
    // Admin filter
    if (adminFilter === "admin" && !u.is_admin) return false;
    if (adminFilter === "member" && u.is_admin) return false;
    
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-serif text-sage-darker">User Management</h1>
        <p className="text-muted-foreground text-sm">{filteredUsers.length} of {users.length} users</p>
      </div>

      {/* Filters */}
      <Card className="bg-sage/5 border-sage/20">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-white border-sage/20"
              />
            </div>
            
            {/* Phase Filter */}
            <Select value={phaseFilter} onValueChange={setPhaseFilter}>
              <SelectTrigger className="bg-white border-sage/20">
                <SelectValue placeholder="Phase" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Phases</SelectItem>
                <SelectItem value="ttc">TTC</SelectItem>
                <SelectItem value="pregnant">Pregnant</SelectItem>
                <SelectItem value="postpartum">Postpartum</SelectItem>
              </SelectContent>
            </Select>

            {/* Interest Filter */}
            <Select value={interestFilter} onValueChange={setInterestFilter}>
              <SelectTrigger className="bg-white border-sage/20">
                <SelectValue placeholder="Interest" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Interests</SelectItem>
                {allInterests.map(interest => (
                  <SelectItem key={interest} value={interest}>{interest}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Admin Filter */}
            <Select value={adminFilter} onValueChange={setAdminFilter}>
              <SelectTrigger className="bg-white border-sage/20">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admins Only</SelectItem>
                <SelectItem value="member">Members Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-white border-sage/20">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-sage/20 bg-sage/5">
                  <th className="text-left p-4 text-muted-foreground font-medium text-sm">User</th>
                  <th className="text-left p-4 text-muted-foreground font-medium text-sm">Phase</th>
                  <th className="text-left p-4 text-muted-foreground font-medium text-sm">Interests</th>
                  <th className="text-left p-4 text-muted-foreground font-medium text-sm">Joined</th>
                  <th className="text-left p-4 text-muted-foreground font-medium text-sm">Role</th>
                  <th className="text-right p-4 text-muted-foreground font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      No users found matching your filters
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => (
                    <tr key={u.user_id} className="border-b border-sage/10 hover:bg-cream">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={u.picture} />
                            <AvatarFallback className="bg-sage/20 text-sage-dark">{u.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sage-darker font-medium">{u.name}</p>
                            <p className="text-muted-foreground text-xs">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className={`phase-${u.phase} text-xs`}>{getPhaseLabel(u.phase)}</Badge>
                      </td>
                      <td className="p-4">
                        {u.interests && u.interests.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {u.interests.slice(0, 3).map((interest, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs border-sage/30 text-sage-dark">
                                {interest}
                              </Badge>
                            ))}
                            {u.interests.length > 3 && (
                              <Badge 
                                variant="outline" 
                                className="text-xs border-sage/30 text-sage-dark cursor-pointer"
                                onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                              >
                                +{u.interests.length - 3} more
                              </Badge>
                            )}
                            {expandedUser === u.user_id && u.interests.length > 3 && (
                              <div className="w-full mt-1">
                                {u.interests.slice(3).map((interest, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs border-sage/30 text-sage-dark mr-1 mb-1">
                                    {interest}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">None</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-muted-foreground">{formatDate(u.created_at)}</span>
                      </td>
                      <td className="p-4">
                        {u.is_admin ? (
                          <Badge className="bg-purple-100 text-purple-600 text-xs">Admin</Badge>
                        ) : (
                          <Badge variant="outline" className="border-sage/30 text-muted-foreground text-xs">Member</Badge>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleToggleAdmin(u.user_id)}
                            className="text-muted-foreground hover:text-sage-dark"
                            title={u.is_admin ? "Remove admin" : "Make admin"}
                            data-testid={`toggle-admin-${u.user_id}`}
                          >
                            <Shield className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteUser(u.user_id)}
                            className="text-red-400 hover:text-red-600"
                            title="Delete user"
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Experts
const AdminExperts = () => {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingExpertId, setEditingExpertId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    specialty: "maternal_mental_health",
    bio: "",
    picture: "",
    email: "",
    credentials: [],
    hourly_rate: 2000,
    availability: []
  });
  const [selectedExpertForSlots, setSelectedExpertForSlots] = useState(null);
  const [slotsDialogOpen, setSlotsDialogOpen] = useState(false);
  const [expertSlots, setExpertSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [newSlot, setNewSlot] = useState({
    date: "",
    start_time: "",
    end_time: "",
    price_inr: "",
    recurring: "none", // none, weekly, monthly
    recurring_weeks: 4 // how many weeks/months to repeat
  });
  const [bulkSlotMode, setBulkSlotMode] = useState(false);
  const [bulkSlotConfig, setBulkSlotConfig] = useState({
    date: "",
    selectedHours: [],
    price_inr: "",
    recurring: "none",
    recurring_weeks: 4
  });
  const [uploading, setUploading] = useState(false);

  // Available time slots for bulk selection (9am to 9pm)
  const AVAILABLE_HOURS = [
    { start: "09:00", end: "10:00", label: "9 - 10 AM" },
    { start: "10:00", end: "11:00", label: "10 - 11 AM" },
    { start: "11:00", end: "12:00", label: "11 AM - 12 PM" },
    { start: "12:00", end: "13:00", label: "12 - 1 PM" },
    { start: "13:00", end: "14:00", label: "1 - 2 PM" },
    { start: "14:00", end: "15:00", label: "2 - 3 PM" },
    { start: "15:00", end: "16:00", label: "3 - 4 PM" },
    { start: "16:00", end: "17:00", label: "4 - 5 PM" },
    { start: "17:00", end: "18:00", label: "5 - 6 PM" },
    { start: "18:00", end: "19:00", label: "6 - 7 PM" },
    { start: "19:00", end: "20:00", label: "7 - 8 PM" },
    { start: "20:00", end: "21:00", label: "8 - 9 PM" }
  ];

  useEffect(() => {
    fetchExperts();
  }, []);

  useEffect(() => {
    if (selectedExpertForSlots) {
      fetchExpertSlots(selectedExpertForSlots.expert_id);
      setSlotsDialogOpen(true);
    }
  }, [selectedExpertForSlots]);

  const fetchExperts = async () => {
    try {
      const response = await axios.get(`${API}/admin/experts`, { withCredentials: true });
      setExperts(response.data);
    } catch (error) {
      console.error("Experts error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpertSlots = async (expertId) => {
    setSlotsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/experts/${expertId}/slots`, { withCredentials: true });
      setExpertSlots(response.data);
    } catch (error) {
      console.error("Slots error:", error);
      toast.error("Failed to load slots");
    } finally {
      setSlotsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "", title: "", specialty: "maternal_mental_health", bio: "", 
      picture: "", email: "", credentials: [], hourly_rate: 2000, availability: []
    });
    setEditMode(false);
    setEditingExpertId(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5MB allowed.");
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Use JPG, PNG, GIF, or WebP.");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, picture: response.data.url }));
      toast.success("Image uploaded!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateSlot = async () => {
    if (!newSlot.date || !newSlot.start_time || !newSlot.end_time) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      const dates = [newSlot.date];
      
      // Generate recurring dates if selected
      if (newSlot.recurring !== "none") {
        const baseDate = new Date(newSlot.date);
        for (let i = 1; i < newSlot.recurring_weeks; i++) {
          const nextDate = new Date(baseDate);
          if (newSlot.recurring === "weekly") {
            nextDate.setDate(nextDate.getDate() + (7 * i));
          } else if (newSlot.recurring === "monthly") {
            nextDate.setMonth(nextDate.getMonth() + i);
          }
          dates.push(nextDate.toISOString().split('T')[0]);
        }
      }
      
      let created = 0;
      for (const date of dates) {
        const startDateTime = new Date(`${date}T${newSlot.start_time}`);
        const endDateTime = new Date(`${date}T${newSlot.end_time}`);
        
        await axios.post(`${API}/admin/time-slots`, {
          expert_id: selectedExpertForSlots.expert_id,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          price_inr: newSlot.price_inr ? parseInt(newSlot.price_inr) : null
        }, { withCredentials: true });
        created++;
      }
      
      toast.success(`${created} slot(s) created`);
      setNewSlot({ date: "", start_time: "", end_time: "", price_inr: "", recurring: "none", recurring_weeks: 4 });
      fetchExpertSlots(selectedExpertForSlots.expert_id);
    } catch (error) {
      toast.error("Failed to create slot");
    }
  };

  const handleBulkCreateSlots = async () => {
    if (!bulkSlotConfig.date || bulkSlotConfig.selectedHours.length === 0) {
      toast.error("Please select a date and at least one time slot");
      return;
    }
    
    try {
      const dates = [bulkSlotConfig.date];
      
      // Generate recurring dates if selected
      if (bulkSlotConfig.recurring !== "none") {
        const baseDate = new Date(bulkSlotConfig.date);
        for (let i = 1; i < bulkSlotConfig.recurring_weeks; i++) {
          const nextDate = new Date(baseDate);
          if (bulkSlotConfig.recurring === "weekly") {
            nextDate.setDate(nextDate.getDate() + (7 * i));
          } else if (bulkSlotConfig.recurring === "monthly") {
            nextDate.setMonth(nextDate.getMonth() + i);
          }
          dates.push(nextDate.toISOString().split('T')[0]);
        }
      }
      
      let created = 0;
      for (const date of dates) {
        for (const hour of bulkSlotConfig.selectedHours) {
          const startDateTime = new Date(`${date}T${hour.start}`);
          const endDateTime = new Date(`${date}T${hour.end}`);
          
          await axios.post(`${API}/admin/time-slots`, {
            expert_id: selectedExpertForSlots.expert_id,
            start_time: startDateTime.toISOString(),
            end_time: endDateTime.toISOString(),
            price_inr: bulkSlotConfig.price_inr ? parseInt(bulkSlotConfig.price_inr) : null
          }, { withCredentials: true });
          created++;
        }
      }
      
      toast.success(`${created} slot(s) created`);
      setBulkSlotConfig({ date: "", selectedHours: [], price_inr: "", recurring: "none", recurring_weeks: 4 });
      fetchExpertSlots(selectedExpertForSlots.expert_id);
    } catch (error) {
      toast.error("Failed to create slots");
    }
  };

  const toggleHourSelection = (hour) => {
    setBulkSlotConfig(prev => {
      const isSelected = prev.selectedHours.some(h => h.start === hour.start);
      if (isSelected) {
        return { ...prev, selectedHours: prev.selectedHours.filter(h => h.start !== hour.start) };
      } else {
        return { ...prev, selectedHours: [...prev.selectedHours, hour] };
      }
    });
  };

  const selectAllHours = () => {
    setBulkSlotConfig(prev => ({ ...prev, selectedHours: [...AVAILABLE_HOURS] }));
  };

  const clearAllHours = () => {
    setBulkSlotConfig(prev => ({ ...prev, selectedHours: [] }));
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm("Delete this slot?")) return;
    try {
      await axios.delete(`${API}/admin/time-slots/${slotId}`, { withCredentials: true });
      toast.success("Slot deleted");
      fetchExpertSlots(selectedExpertForSlots.expert_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete slot");
    }
  };

  const handleDeleteAllSlots = async () => {
    if (!window.confirm("Delete ALL unbooked slots for this expert? This cannot be undone.")) return;
    try {
      const response = await axios.delete(`${API}/admin/experts/${selectedExpertForSlots.expert_id}/slots`, { withCredentials: true });
      toast.success(response.data.message);
      fetchExpertSlots(selectedExpertForSlots.expert_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete slots");
    }
  };

  const handleCleanupDuplicates = async () => {
    try {
      const response = await axios.post(`${API}/admin/experts/${selectedExpertForSlots.expert_id}/cleanup-duplicates`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchExpertSlots(selectedExpertForSlots.expert_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cleanup duplicates");
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/experts`, formData, { withCredentials: true });
      toast.success("Expert created");
      setDialogOpen(false);
      resetForm();
      fetchExperts();
    } catch (error) {
      toast.error("Failed to create expert");
    }
  };

  const handleEdit = (expert) => {
    setFormData({
      name: expert.name || "",
      title: expert.title || "",
      specialty: expert.specialty || "maternal_mental_health",
      bio: expert.bio || "",
      picture: expert.picture || "",
      email: expert.email || "",
      credentials: expert.credentials || [],
      hourly_rate: expert.hourly_rate || 2000,
      availability: expert.availability || []
    });
    setEditMode(true);
    setEditingExpertId(expert.expert_id);
    setDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/admin/experts/${editingExpertId}`, formData, { withCredentials: true });
      toast.success("Expert updated");
      setDialogOpen(false);
      resetForm();
      fetchExperts();
    } catch (error) {
      toast.error("Failed to update expert");
    }
  };

  const handleToggleActive = async (expertId) => {
    try {
      await axios.put(`${API}/admin/experts/${expertId}/toggle-active`, {}, { withCredentials: true });
      toast.success("Expert status updated");
      fetchExperts();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (expertId) => {
    if (!window.confirm("Delete this expert?")) return;
    try {
      await axios.delete(`${API}/admin/experts/${expertId}`, { withCredentials: true });
      toast.success("Expert deleted");
      fetchExperts();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const getSpecialtyLabel = (specialty) => {
    switch (specialty) {
      case 'maternal_mental_health': return 'Mental Health';
      case 'life_coaching': return 'Life Coach';
      case 'postpartum_support': return 'Postpartum Support';
      case 'physical_fitness': return 'Physical Fitness';
      case 'lactation_consultant': return 'Lactation Consultant';
      case 'nutrition': return 'Nutrition';
      case 'sleep_consultant': return 'Sleep Consultant';
      default: return specialty;
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-experts">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-serif text-sage-darker">Expert Management</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-sage text-white hover:bg-sage-dark rounded-full" data-testid="create-expert-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Expert
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-sage/20 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-sage-darker font-serif">
                {editMode ? "Edit Expert" : "Add New Expert"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sage-darker">Name</Label>
                  <Input 
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-cream border-sage/20"
                    data-testid="expert-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sage-darker">Title</Label>
                  <Input 
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="bg-cream border-sage/20"
                    placeholder="e.g., Licensed Therapist"
                    data-testid="expert-title-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Email</Label>
                <Input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-cream border-sage/20"
                  placeholder="expert@example.com"
                  data-testid="expert-email-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sage-darker">Specialty</Label>
                  <Select value={formData.specialty} onValueChange={(v) => setFormData(prev => ({ ...prev, specialty: v }))}>
                    <SelectTrigger className="bg-cream border-sage/20" data-testid="expert-specialty-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maternal_mental_health">Mental Health Therapist</SelectItem>
                      <SelectItem value="life_coaching">Life Coach</SelectItem>
                      <SelectItem value="postpartum_support">Postpartum Specialist</SelectItem>
                      <SelectItem value="physical_fitness">Physical Fitness</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sage-darker">Hourly Rate (₹ INR)</Label>
                  <Input 
                    type="number"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: parseInt(e.target.value) || 0 }))}
                    className="bg-cream border-sage/20"
                    placeholder="2000"
                    data-testid="expert-rate-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Bio</Label>
                <Textarea 
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  className="bg-cream border-sage/20"
                  rows={3}
                  data-testid="expert-bio-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Profile Image</Label>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Input 
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="bg-cream border-sage/20 file:mr-3 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-sm file:bg-sage/20 file:text-sage-dark hover:file:bg-sage/30"
                      data-testid="expert-picture-upload"
                      disabled={uploading}
                    />
                    {uploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg">
                        <div className="animate-spin w-5 h-5 border-2 border-sage border-t-transparent rounded-full" />
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Max 5MB. JPG, PNG, GIF, WebP</p>
                {formData.picture && (
                  <div className="mt-2 flex justify-center">
                    <img 
                      src={formData.picture.startsWith('/api') ? `${API.replace('/api', '')}${formData.picture}` : formData.picture} 
                      alt="Expert preview" 
                      className="w-24 h-24 rounded-full object-cover border-2 border-sage/20"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
              <Button 
                onClick={editMode ? handleUpdate : handleCreate} 
                className="w-full bg-sage text-white hover:bg-sage-dark" 
                data-testid="submit-expert-btn"
              >
                {editMode ? "Update Expert" : "Create Expert"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        {experts.map((expert) => (
          <Card key={expert.expert_id} className="bg-white border-sage/20">
            <CardContent className="p-5">
              <div className="flex gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={expert.picture} />
                  <AvatarFallback className="bg-sage/20 text-sage-dark text-lg">{expert.name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sage-darker font-medium">{expert.name}</h3>
                      <p className="text-muted-foreground text-sm">{expert.title}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEdit(expert)}
                        className="text-sage-dark"
                        data-testid={`edit-expert-${expert.expert_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleToggleActive(expert.expert_id)}
                        className={expert.is_active ? "text-green-500" : "text-gray-400"}
                        data-testid={`toggle-expert-${expert.expert_id}`}
                      >
                        {expert.is_active ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(expert.expert_id)}
                        className="text-red-400"
                        data-testid={`delete-expert-${expert.expert_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <Badge variant="outline" className="border-sage/30 text-sage-dark text-xs">
                      {getSpecialtyLabel(expert.specialty)}
                    </Badge>
                    <span className="flex items-center gap-1 text-yellow-600 text-sm">
                      <Star className="w-3 h-3 fill-current" />
                      {expert.rating?.toFixed(1)}
                    </span>
                    <span className="text-muted-foreground text-sm">₹{expert.hourly_rate?.toLocaleString('en-IN')}/hr</span>
                  </div>
                  <p className="text-muted-foreground text-sm mt-2 line-clamp-2">{expert.bio}</p>
                  <div className="mt-3">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-sage/30 text-sage-dark"
                          onClick={() => setSelectedExpertForSlots(expert)}
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          Manage Slots
                        </Button>
                      </DialogTrigger>
                    </Dialog>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Slots Management Dialog */}
      <Dialog open={slotsDialogOpen} onOpenChange={(open) => { 
        setSlotsDialogOpen(open); 
        if (!open) {
          setSelectedExpertForSlots(null);
          setBulkSlotMode(false);
        }
      }}>
        <DialogContent className="bg-white border-sage/20 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sage-darker font-serif">
              Manage Slots for {selectedExpertForSlots?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Mode Toggle */}
            <div className="flex gap-2">
              <Button 
                variant={!bulkSlotMode ? "default" : "outline"}
                onClick={() => setBulkSlotMode(false)}
                className={!bulkSlotMode ? "bg-sage text-white" : "border-sage/30"}
                size="sm"
              >
                Single Slot
              </Button>
              <Button 
                variant={bulkSlotMode ? "default" : "outline"}
                onClick={() => setBulkSlotMode(true)}
                className={bulkSlotMode ? "bg-sage text-white" : "border-sage/30"}
                size="sm"
              >
                Bulk Add (Quick)
              </Button>
            </div>

            {!bulkSlotMode ? (
              /* Single Slot Form */
              <Card className="bg-sage/5 border-sage/20">
                <CardContent className="p-4">
                  <h4 className="font-medium text-sage-darker mb-3">Add Single Time Slot</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-sage-dark">Date</Label>
                      <Input
                        type="date"
                        value={newSlot.date}
                        onChange={(e) => setNewSlot(prev => ({ ...prev, date: e.target.value }))}
                        className="bg-white border-sage/20"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-sage-dark">Start Time</Label>
                      <Input
                        type="time"
                        value={newSlot.start_time}
                        onChange={(e) => setNewSlot(prev => ({ ...prev, start_time: e.target.value }))}
                        className="bg-white border-sage/20"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-sage-dark">End Time</Label>
                      <Input
                        type="time"
                        value={newSlot.end_time}
                        onChange={(e) => setNewSlot(prev => ({ ...prev, end_time: e.target.value }))}
                        className="bg-white border-sage/20"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-sage-dark">Price (₹)</Label>
                      <Input
                        type="number"
                        value={newSlot.price_inr}
                        onChange={(e) => setNewSlot(prev => ({ ...prev, price_inr: e.target.value }))}
                        className="bg-white border-sage/20"
                        placeholder="Auto"
                      />
                    </div>
                  </div>
                  
                  {/* Recurring Options */}
                  <div className="mt-4 p-3 bg-white rounded-lg border border-sage/20">
                    <Label className="text-xs text-sage-dark mb-2 block">Repeat this slot</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant={newSlot.recurring === "none" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setNewSlot(prev => ({ ...prev, recurring: "none" }))}
                        className={newSlot.recurring === "none" ? "bg-sage text-white" : "border-sage/30"}
                      >
                        No repeat
                      </Button>
                      <Button
                        type="button"
                        variant={newSlot.recurring === "weekly" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setNewSlot(prev => ({ ...prev, recurring: "weekly" }))}
                        className={newSlot.recurring === "weekly" ? "bg-sage text-white" : "border-sage/30"}
                      >
                        Weekly
                      </Button>
                      <Button
                        type="button"
                        variant={newSlot.recurring === "monthly" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setNewSlot(prev => ({ ...prev, recurring: "monthly" }))}
                        className={newSlot.recurring === "monthly" ? "bg-sage text-white" : "border-sage/30"}
                      >
                        Monthly
                      </Button>
                      {newSlot.recurring !== "none" && (
                        <div className="flex items-center gap-2 ml-2">
                          <span className="text-xs text-muted-foreground">for</span>
                          <Input
                            type="number"
                            value={newSlot.recurring_weeks}
                            onChange={(e) => setNewSlot(prev => ({ ...prev, recurring_weeks: parseInt(e.target.value) || 1 }))}
                            className="w-16 h-8 bg-white border-sage/20 text-center"
                            min={1}
                            max={12}
                          />
                          <span className="text-xs text-muted-foreground">{newSlot.recurring === "weekly" ? "weeks" : "months"}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleCreateSlot}
                    className="mt-3 bg-sage text-white hover:bg-sage-dark"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {newSlot.recurring !== "none" ? `Add ${newSlot.recurring_weeks} Slots` : "Add Slot"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              /* Bulk Slot Form */
              <Card className="bg-sage/5 border-sage/20">
                <CardContent className="p-4">
                  <h4 className="font-medium text-sage-darker mb-3">Quick Add Multiple 1-Hour Slots</h4>
                  
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-sage-dark">Date</Label>
                      <Input
                        type="date"
                        value={bulkSlotConfig.date}
                        onChange={(e) => setBulkSlotConfig(prev => ({ ...prev, date: e.target.value }))}
                        className="bg-white border-sage/20"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-sage-dark">Price per slot (₹)</Label>
                      <Input
                        type="number"
                        value={bulkSlotConfig.price_inr}
                        onChange={(e) => setBulkSlotConfig(prev => ({ ...prev, price_inr: e.target.value }))}
                        className="bg-white border-sage/20"
                        placeholder="Auto"
                      />
                    </div>
                  </div>

                  {/* Time Slot Selection Grid */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-sage-dark">Select Time Slots (9 AM - 9 PM)</Label>
                      <div className="flex gap-2">
                        <Button variant="link" size="sm" onClick={selectAllHours} className="text-sage-dark h-auto p-0 text-xs">
                          Select All
                        </Button>
                        <Button variant="link" size="sm" onClick={clearAllHours} className="text-muted-foreground h-auto p-0 text-xs">
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                      {AVAILABLE_HOURS.map((hour) => {
                        const isSelected = bulkSlotConfig.selectedHours.some(h => h.start === hour.start);
                        return (
                          <button
                            key={hour.start}
                            onClick={() => toggleHourSelection(hour)}
                            className={`p-2 rounded-lg border text-xs font-medium transition-all ${
                              isSelected 
                                ? 'bg-sage text-white border-sage' 
                                : 'bg-white border-sage/20 text-sage-dark hover:border-sage/40'
                            }`}
                          >
                            {hour.label}
                          </button>
                        );
                      })}
                    </div>
                    {bulkSlotConfig.selectedHours.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {bulkSlotConfig.selectedHours.length} slot(s) selected
                      </p>
                    )}
                  </div>

                  {/* Recurring Options */}
                  <div className="p-3 bg-white rounded-lg border border-sage/20 mb-4">
                    <Label className="text-xs text-sage-dark mb-2 block">Repeat these slots</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant={bulkSlotConfig.recurring === "none" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setBulkSlotConfig(prev => ({ ...prev, recurring: "none" }))}
                        className={bulkSlotConfig.recurring === "none" ? "bg-sage text-white" : "border-sage/30"}
                      >
                        No repeat
                      </Button>
                      <Button
                        type="button"
                        variant={bulkSlotConfig.recurring === "weekly" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setBulkSlotConfig(prev => ({ ...prev, recurring: "weekly" }))}
                        className={bulkSlotConfig.recurring === "weekly" ? "bg-sage text-white" : "border-sage/30"}
                      >
                        Weekly
                      </Button>
                      <Button
                        type="button"
                        variant={bulkSlotConfig.recurring === "monthly" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setBulkSlotConfig(prev => ({ ...prev, recurring: "monthly" }))}
                        className={bulkSlotConfig.recurring === "monthly" ? "bg-sage text-white" : "border-sage/30"}
                      >
                        Monthly
                      </Button>
                      {bulkSlotConfig.recurring !== "none" && (
                        <div className="flex items-center gap-2 ml-2">
                          <span className="text-xs text-muted-foreground">for</span>
                          <Input
                            type="number"
                            value={bulkSlotConfig.recurring_weeks}
                            onChange={(e) => setBulkSlotConfig(prev => ({ ...prev, recurring_weeks: parseInt(e.target.value) || 1 }))}
                            className="w-16 h-8 bg-white border-sage/20 text-center"
                            min={1}
                            max={12}
                          />
                          <span className="text-xs text-muted-foreground">{bulkSlotConfig.recurring === "weekly" ? "weeks" : "months"}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleBulkCreateSlots}
                    className="bg-sage text-white hover:bg-sage-dark"
                    size="sm"
                    disabled={bulkSlotConfig.selectedHours.length === 0}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Create {bulkSlotConfig.recurring !== "none" 
                      ? `${bulkSlotConfig.selectedHours.length * bulkSlotConfig.recurring_weeks} Slots` 
                      : `${bulkSlotConfig.selectedHours.length} Slot(s)`}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Existing Slots */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sage-darker">
                  Available Slots ({expertSlots.length})
                </h4>
                {expertSlots.length > 0 && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleCleanupDuplicates}
                      className="text-xs border-amber-300 text-amber-600 hover:bg-amber-50"
                    >
                      Remove Duplicates
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleDeleteAllSlots}
                      className="text-xs border-red-300 text-red-500 hover:bg-red-50"
                    >
                      Delete All
                    </Button>
                  </div>
                )}
              </div>
              
              {slotsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-2 border-sage border-t-transparent rounded-full mx-auto" />
                </div>
              ) : expertSlots.length === 0 ? (
                <div className="text-center py-8 bg-cream rounded-xl">
                  <Calendar className="w-12 h-12 text-sage/40 mx-auto mb-2" />
                  <p className="text-muted-foreground">No slots created yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {expertSlots.map((slot) => {
                    const startDate = new Date(slot.start_time);
                    const endDate = new Date(slot.end_time);
                    const isPast = startDate < new Date();
                    
                    return (
                      <div 
                        key={slot.slot_id}
                        className={`flex items-center justify-between p-3 rounded-xl border ${
                          slot.is_booked 
                            ? 'bg-green-50 border-green-200' 
                            : isPast 
                              ? 'bg-gray-50 border-gray-200 opacity-50' 
                              : 'bg-white border-sage/20'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-center min-w-[60px]">
                            <p className="text-sage-dark font-bold">{startDate.getDate()}</p>
                            <p className="text-xs text-muted-foreground">
                              {startDate.toLocaleDateString('en', { month: 'short' })}
                            </p>
                          </div>
                          <div>
                            <p className="text-sage-darker font-medium">
                              {startDate.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })} - 
                              {endDate.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {slot.duration_minutes} min • ₹{(slot.price_inr || 0).toLocaleString('en-IN')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {slot.is_booked ? (
                            <Badge className="bg-green-100 text-green-700">Booked</Badge>
                          ) : isPast ? (
                            <Badge variant="outline" className="border-gray-300 text-gray-500">Past</Badge>
                          ) : (
                            <Badge variant="outline" className="border-sage/30 text-sage-dark">Available</Badge>
                          )}
                          {!slot.is_booked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSlot(slot.slot_id)}
                              className="text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Content
const AdminContent = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    content_type: "article",
    category: "general",
    image_url: "",
    video_url: "",
    phase: "",
    is_published: true
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`, { withCredentials: true });
      setContent(response.data);
    } catch (error) {
      console.error("Content error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/content`, formData, { withCredentials: true });
      toast.success("Content created");
      setDialogOpen(false);
      setFormData({ title: "", description: "", content_type: "article", category: "general", image_url: "", video_url: "", phase: "", is_published: true });
      fetchContent();
    } catch (error) {
      toast.error("Failed to create content");
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm("Delete this content?")) return;
    try {
      await axios.delete(`${API}/admin/content/${contentId}`, { withCredentials: true });
      toast.success("Content deleted");
      fetchContent();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-content">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-serif text-sage-darker">Content Management</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-sage text-white hover:bg-sage-dark rounded-full" data-testid="create-content-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Content
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-sage/20 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-sage-darker font-serif">Create Content</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-sage-darker">Title</Label>
                <Input 
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="bg-cream border-sage/20"
                  data-testid="content-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Description</Label>
                <Textarea 
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-cream border-sage/20"
                  data-testid="content-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sage-darker">Type</Label>
                  <Select value={formData.content_type} onValueChange={(v) => setFormData(prev => ({ ...prev, content_type: v }))}>
                    <SelectTrigger className="bg-cream border-sage/20" data-testid="content-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="article">Article</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="session">Session</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sage-darker">Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger className="bg-cream border-sage/20" data-testid="content-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="fitness">Fitness</SelectItem>
                      <SelectItem value="nutrition">Nutrition</SelectItem>
                      <SelectItem value="mental_health">Mental Health</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Image URL</Label>
                <Input 
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  className="bg-cream border-sage/20"
                  placeholder="https://..."
                  data-testid="content-image-input"
                />
              </div>
              <Button onClick={handleCreate} className="w-full bg-sage text-white hover:bg-sage-dark" data-testid="submit-content-btn">
                Create Content
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {content.map((item) => (
          <Card key={item.content_id} className="bg-white border-sage/20 overflow-hidden">
            <div className="h-32 bg-sage/10">
              {item.image_url && <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />}
            </div>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <Badge className="mb-2 capitalize bg-sage/10 text-sage-dark">{item.content_type}</Badge>
                  <h3 className="text-sage-darker font-medium truncate">{item.title}</h3>
                  <p className="text-muted-foreground text-sm truncate">{item.description}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(item.content_id)} className="text-red-400" data-testid={`delete-content-${item.content_id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Admin Events
const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_type: "session",
    category: "general",
    image_url: "",
    scheduled_at: "",
    duration_minutes: 60,
    host_name: "",
    is_published: true,
    total_slots: "",
    price_inr: 0,
    location: ""
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(response.data);
    } catch (error) {
      console.error("Events error:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ 
      title: "", description: "", event_type: "session", category: "general", 
      image_url: "", scheduled_at: "", duration_minutes: 60, host_name: "", 
      is_published: true, total_slots: "", price_inr: 0, location: "" 
    });
    setEditMode(false);
    setEditingEventId(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5MB allowed.");
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Use JPG, PNG, GIF, or WebP.");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, image_url: response.data.url }));
      toast.success("Image uploaded!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        total_slots: formData.total_slots ? parseInt(formData.total_slots) : null,
        scheduled_at: formData.scheduled_at ? new Date(formData.scheduled_at).toISOString() : null
      };
      await axios.post(`${API}/admin/events`, payload, { withCredentials: true });
      toast.success("Event created");
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      console.error("Create event error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to create event");
    }
  };

  const handleEdit = (event) => {
    setFormData({
      title: event.title || "",
      description: event.description || "",
      event_type: event.event_type || "session",
      category: event.category || "general",
      image_url: event.image_url || "",
      scheduled_at: event.scheduled_at ? new Date(event.scheduled_at).toISOString().slice(0, 16) : "",
      duration_minutes: event.duration_minutes || 60,
      host_name: event.host_name || "",
      is_published: event.is_published !== false,
      total_slots: event.total_slots || "",
      price_inr: event.price_inr || 0,
      location: event.location || ""
    });
    setEditMode(true);
    setEditingEventId(event.event_id);
    setDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        ...formData,
        total_slots: formData.total_slots ? parseInt(formData.total_slots) : null,
        scheduled_at: formData.scheduled_at ? new Date(formData.scheduled_at).toISOString() : null
      };
      await axios.put(`${API}/admin/events/${editingEventId}`, payload, { withCredentials: true });
      toast.success("Event updated");
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error("Failed to update event");
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/admin/events/${eventId}`, { withCredentials: true });
      toast.success("Event deleted");
      fetchEvents();
    } catch (error) {
      console.error("Delete event error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to delete event");
    }
  };

  const generateMeetLink = async (eventId) => {
    try {
      const response = await axios.post(`${API}/admin/events/${eventId}/generate-meet`, {}, { withCredentials: true });
      toast.success("Meet link generated!");
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate Meet link");
    }
  };

  const copyMeetLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Meet link copied to clipboard!");
  };

  return (
    <div className="space-y-6" data-testid="admin-events">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-serif text-sage-darker">Event Management</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-sage text-white hover:bg-sage-dark rounded-full" data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-sage/20 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-sage-darker font-serif">
                {editMode ? "Edit Event" : "Create Event"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-sage-darker">Title</Label>
                <Input 
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="bg-cream border-sage/20"
                  data-testid="event-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Description</Label>
                <Textarea 
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-cream border-sage/20"
                  data-testid="event-description-input"
                />
              </div>
              
              {/* Image Upload field */}
              <div className="space-y-2">
                <Label className="text-sage-darker">Event Image</Label>
                <div className="relative">
                  <Input 
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className="bg-cream border-sage/20 file:mr-3 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-sm file:bg-sage/20 file:text-sage-dark hover:file:bg-sage/30"
                    data-testid="event-image-upload"
                    disabled={uploading}
                  />
                  {uploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg">
                      <div className="animate-spin w-5 h-5 border-2 border-sage border-t-transparent rounded-full" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Max 5MB. JPG, PNG, GIF, WebP</p>
                {formData.image_url && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-sage/20">
                    <img 
                      src={formData.image_url.startsWith('/api') ? `${API.replace('/api', '')}${formData.image_url}` : formData.image_url} 
                      alt="Event preview" 
                      className="w-full h-32 object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sage-darker">Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger className="bg-cream border-sage/20" data-testid="event-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="fitness">Fitness</SelectItem>
                      <SelectItem value="nutrition">Nutrition</SelectItem>
                      <SelectItem value="mental_health">Mental Health</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sage-darker">Duration (min)</Label>
                  <Input 
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))}
                    className="bg-cream border-sage/20"
                    data-testid="event-duration-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Host Name</Label>
                <Input 
                  value={formData.host_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, host_name: e.target.value }))}
                  className="bg-cream border-sage/20"
                  data-testid="event-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Date & Time</Label>
                <Input 
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduled_at: e.target.value }))}
                  className="bg-cream border-sage/20"
                  data-testid="event-datetime-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sage-darker">Price (₹)</Label>
                  <Input 
                    type="number"
                    value={formData.price_inr}
                    onChange={(e) => setFormData(prev => ({ ...prev, price_inr: parseInt(e.target.value) || 0 }))}
                    className="bg-cream border-sage/20"
                    placeholder="0 for free events"
                    data-testid="event-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sage-darker">Total Slots</Label>
                  <Input 
                    type="number"
                    value={formData.total_slots}
                    onChange={(e) => setFormData(prev => ({ ...prev, total_slots: e.target.value }))}
                    className="bg-cream border-sage/20"
                    placeholder="Leave empty for unlimited"
                    data-testid="event-slots-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Location</Label>
                <Input 
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  className="bg-cream border-sage/20"
                  placeholder="Online or venue address"
                  data-testid="event-location-input"
                />
              </div>
              <Button 
                onClick={editMode ? handleUpdate : handleCreate} 
                className="w-full bg-sage text-white hover:bg-sage-dark" 
                data-testid="submit-event-btn"
              >
                {editMode ? "Update Event" : "Create Event"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="space-y-4">
        {events.map((event) => (
          <Card key={event.event_id} className="bg-white border-sage/20 overflow-hidden">
            <CardContent className="p-0">
              <div className="flex">
                {/* Event Image */}
                <div className="w-24 h-24 flex-shrink-0 bg-sage/10">
                  {event.image_url ? (
                    <img 
                      src={event.image_url} 
                      alt={event.title} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <p className="text-sage-dark font-bold text-xl">{new Date(event.scheduled_at).getDate()}</p>
                      <p className="text-sage/70 text-xs">{new Date(event.scheduled_at).toLocaleDateString('en', { month: 'short' })}</p>
                    </div>
                  )}
                </div>
                
                {/* Event Details */}
                <div className="flex-1 p-4 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sage-darker font-medium">{event.title}</h3>
                      {event.meet_link && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">
                          <Video className="w-3 h-3 mr-1" />
                          Meet
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {event.host_name} • {event.attendee_count}{event.total_slots ? `/${event.total_slots}` : ''} attending
                      {event.price_inr > 0 && <span className="ml-2 text-sage-dark font-medium">₹{event.price_inr.toLocaleString('en-IN')}</span>}
                      {event.price_inr === 0 && <span className="ml-2 text-green-600 text-xs">Free</span>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(event.scheduled_at).toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      {event.total_slots && event.attendee_count >= event.total_slots && (
                        <span className="ml-2 text-red-500 font-medium">• FULL</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* View Registrations */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate(`/admin/events/${event.event_id}/registrations`)} 
                      className="text-sage-dark border-sage/20 hover:bg-sage/10"
                    >
                      <Users className="w-3 h-3 mr-1" />
                      {event.attendee_count || 0}
                    </Button>
                    {/* Meet Link Actions */}
                    {event.meet_link ? (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => copyMeetLink(event.meet_link)} 
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy Link
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => generateMeetLink(event.event_id)} 
                        className="text-sage-dark border-sage/20 hover:bg-sage/10"
                      >
                        <Video className="w-3 h-3 mr-1" />
                        Generate Meet
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleEdit(event)} 
                      className="text-sage-dark"
                      data-testid={`edit-event-${event.event_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(event.event_id)} 
                      className="text-red-400" 
                      data-testid={`delete-event-${event.event_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Admin Bookings - View all expert session bookings
const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, confirmed, pending, cancelled
  const navigate = useNavigate();

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings`, { withCredentials: true });
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const filteredBookings = bookings.filter(b => {
    if (filter === "all") return true;
    return b.status === filter || b.payment_status === filter;
  });

  if (loading) {
    return <div className="animate-pulse space-y-4">
      {[1,2,3].map(i => <div key={i} className="h-20 bg-sage/10 rounded-xl" />)}
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-serif text-sage-darker">Expert Session Bookings</h1>
          <p className="text-muted-foreground text-sm mt-1">View all user bookings for expert sessions</p>
        </div>
        <div className="flex gap-2">
          {["all", "confirmed", "pending", "cancelled"].map(f => (
            <Button
              key={f}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? "bg-sage text-white" : ""}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sage/10 border-b border-sage/20">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-sage-darker">User</th>
                  <th className="text-left p-4 text-sm font-medium text-sage-darker">Expert</th>
                  <th className="text-left p-4 text-sm font-medium text-sage-darker">Session Time</th>
                  <th className="text-left p-4 text-sm font-medium text-sage-darker">Amount</th>
                  <th className="text-left p-4 text-sm font-medium text-sage-darker">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-sage-darker">Meet Link</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      No bookings found
                    </td>
                  </tr>
                ) : (
                  filteredBookings.map((booking) => (
                    <tr key={booking.booking_id} className="border-b border-sage/10 hover:bg-sage/5">
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-sage-darker">{booking.user_name || "Unknown"}</p>
                          <p className="text-xs text-muted-foreground">{booking.user_email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-sage-darker">{booking.expert_name || "Unknown"}</p>
                          <p className="text-xs text-muted-foreground">{booking.expert_title}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-sm">{formatDate(booking.slot_start)}</p>
                      </td>
                      <td className="p-4">
                        <p className="font-medium">₹{(booking.amount_inr || booking.slot_price || 0).toLocaleString('en-IN')}</p>
                      </td>
                      <td className="p-4">
                        <Badge className={`text-xs ${
                          booking.status === 'confirmed' || booking.payment_status === 'completed'
                            ? 'bg-green-100 text-green-700'
                            : booking.status === 'cancelled'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {booking.status || booking.payment_status || 'pending'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {booking.meet_link ? (
                          <a 
                            href={booking.meet_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                          >
                            <Video className="w-3 h-3" />
                            Join
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <p className="text-sm text-muted-foreground">
        Total: {filteredBookings.length} booking(s)
      </p>
    </div>
  );
};

// Admin Event Registrations - View users registered for a specific event
const AdminEventRegistrations = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ event: null, registrations: [], total_registered: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRegistrations = async () => {
      if (!eventId) return;
      try {
        const response = await axios.get(`${API}/admin/events/${eventId}/registrations`, { withCredentials: true });
        setData(response.data);
      } catch (error) {
        console.error("Fetch registrations error:", error);
        toast.error(error.response?.data?.detail || "Failed to load registrations");
      } finally {
        setLoading(false);
      }
    };
    
    fetchRegistrations();
  }, [eventId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">
      {[1,2,3].map(i => <div key={i} className="h-20 bg-sage/10 rounded-xl" />)}
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/events')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>
      </div>

      {data.event && (
        <Card className="bg-sage/5 border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              {data.event.image_url && (
                <img 
                  src={data.event.image_url} 
                  alt={data.event.title} 
                  className="w-24 h-24 object-cover rounded-lg"
                />
              )}
              <div className="flex-1">
                <h1 className="text-xl font-serif text-sage-darker">{data.event.title}</h1>
                <p className="text-muted-foreground text-sm mt-1">
                  {formatDate(data.event.scheduled_at)} • {data.event.host_name}
                </p>
                <div className="flex items-center gap-4 mt-2">
                  <Badge className="bg-sage/20 text-sage-dark">
                    {data.total_registered}{data.event.total_slots ? `/${data.event.total_slots}` : ''} registered
                  </Badge>
                  {data.event.price_inr > 0 && (
                    <span className="text-sm font-medium">₹{data.event.price_inr.toLocaleString('en-IN')}</span>
                  )}
                  {data.event.meet_link && (
                    <a 
                      href={data.event.meet_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                    >
                      <Video className="w-3 h-3" />
                      Meet Link
                    </a>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Registered Users ({data.total_registered})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sage/10 border-b border-sage/20">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-sage-darker">User</th>
                  <th className="text-left p-4 text-sm font-medium text-sage-darker">Email</th>
                  <th className="text-left p-4 text-sm font-medium text-sage-darker">Registered At</th>
                  <th className="text-left p-4 text-sm font-medium text-sage-darker">Payment Status</th>
                </tr>
              </thead>
              <tbody>
                {data.registrations.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-muted-foreground">
                      No registrations yet
                    </td>
                  </tr>
                ) : (
                  data.registrations.map((reg) => (
                    <tr key={reg.registration_id} className="border-b border-sage/10 hover:bg-sage/5">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            {reg.user_picture && <AvatarImage src={reg.user_picture} />}
                            <AvatarFallback className="bg-sage/20 text-sage-dark text-xs">
                              {reg.user_name?.[0] || "?"}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium text-sage-darker">{reg.user_name || "Unknown"}</span>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">{reg.user_email}</td>
                      <td className="p-4 text-sm">{formatDate(reg.registered_at)}</td>
                      <td className="p-4">
                        <Badge className={`text-xs ${
                          reg.payment_status === 'completed' || reg.payment_status === 'free'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {reg.payment_status || 'pending'}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin App Settings (Phases & Interests)
const AdminAppSettings = () => {
  const [config, setConfig] = useState({ phases: [], interests: [] });
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    option_type: "phase",
    value: "",
    label: "",
    description: "",
    icon: "Sparkles",
    color: "sage",
    order: 0
  });
  const [reorderMode, setReorderMode] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/config-options`, { withCredentials: true });
      // Sort by order
      const sortedConfig = {
        phases: response.data.phases.sort((a, b) => (a.order || 0) - (b.order || 0)),
        interests: response.data.interests.sort((a, b) => (a.order || 0) - (b.order || 0))
      };
      setConfig(sortedConfig);
    } catch (error) {
      console.error("Config error:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/config-options`, formData, { withCredentials: true });
      toast.success(`${formData.option_type === 'phase' ? 'Phase' : 'Interest'} created`);
      setDialogOpen(false);
      setFormData({ option_type: "phase", value: "", label: "", description: "", icon: "Sparkles", color: "sage", order: 0 });
      fetchConfig();
    } catch (error) {
      toast.error("Failed to create");
    }
  };

  const handleUpdate = async (optionId, updates) => {
    try {
      await axios.put(`${API}/admin/config-options/${optionId}`, updates, { withCredentials: true });
      toast.success("Updated successfully");
      setEditingItem(null);
      fetchConfig();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleToggleActive = async (option) => {
    await handleUpdate(option.option_id, { is_active: !option.is_active });
  };

  const handleDelete = async (optionId) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/admin/config-options/${optionId}`, { withCredentials: true });
      toast.success("Deleted");
      fetchConfig();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all phases and interests to defaults?")) return;
    try {
      await axios.post(`${API}/admin/config-options/reset`, {}, { withCredentials: true });
      toast.success("Reset to defaults");
      fetchConfig();
    } catch (error) {
      toast.error("Failed to reset");
    }
  };

  const handleMoveItem = async (optionType, fromIndex, toIndex) => {
    const items = optionType === 'phase' ? [...config.phases] : [...config.interests];
    const [movedItem] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, movedItem);
    
    // Update local state immediately for better UX
    if (optionType === 'phase') {
      setConfig(prev => ({ ...prev, phases: items }));
    } else {
      setConfig(prev => ({ ...prev, interests: items }));
    }
    
    // Save new order to backend
    try {
      await axios.post(`${API}/admin/config-options/reorder`, {
        option_type: optionType,
        order: items.map(item => item.option_id)
      }, { withCredentials: true });
    } catch (error) {
      console.error("Reorder error:", error);
      toast.error("Failed to save order");
      fetchConfig(); // Revert on error
    }
  };

  const ICONS = ["Sparkles", "Heart", "Baby", "Star", "Users", "Brain"];
  const COLORS = ["sage", "clay", "purple", "blue", "pink", "orange"];

  if (loading) {
    return <div className="animate-pulse space-y-4">
      {[1,2].map(i => <div key={i} className="h-48 bg-sage/10 rounded-xl" />)}
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-app-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif text-sage-darker">App Settings</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage motherhood phases and interest options</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sage text-white hover:bg-sage-dark rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Option
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-sage/20 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-sage-darker font-serif">Add New Option</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-sage-darker">Type</Label>
                  <Select value={formData.option_type} onValueChange={(v) => setFormData(prev => ({ ...prev, option_type: v }))}>
                    <SelectTrigger className="bg-cream border-sage/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phase">Motherhood Phase</SelectItem>
                      <SelectItem value="interest">Interest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.option_type === "phase" && (
                  <div className="space-y-2">
                    <Label className="text-sage-darker">Value (ID)</Label>
                    <Input 
                      value={formData.value}
                      onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                      className="bg-cream border-sage/20"
                      placeholder="e.g., ttc, pregnant"
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label className="text-sage-darker">Label</Label>
                  <Input 
                    value={formData.label}
                    onChange={(e) => {
                      const label = e.target.value;
                      setFormData(prev => ({ 
                        ...prev, 
                        label,
                        value: prev.option_type === "interest" ? label : prev.value 
                      }));
                    }}
                    className="bg-cream border-sage/20"
                    placeholder="Display name"
                  />
                </div>
                
                {formData.option_type === "phase" && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-sage-darker">Description</Label>
                      <Textarea 
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="bg-cream border-sage/20"
                        placeholder="Brief description"
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sage-darker">Icon</Label>
                        <Select value={formData.icon} onValueChange={(v) => setFormData(prev => ({ ...prev, icon: v }))}>
                          <SelectTrigger className="bg-cream border-sage/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ICONS.map(icon => (
                              <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sage-darker">Color</Label>
                        <Select value={formData.color} onValueChange={(v) => setFormData(prev => ({ ...prev, color: v }))}>
                          <SelectTrigger className="bg-cream border-sage/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COLORS.map(color => (
                              <SelectItem key={color} value={color}>{color}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}
                
                <Button onClick={handleCreate} className="w-full bg-sage text-white hover:bg-sage-dark">
                  Create
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button 
            variant="outline"
            onClick={handleReset}
            className="border-red-200 text-red-500 hover:bg-red-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </Button>
        </div>
      </div>

      {/* Phases Section */}
      <Card className="bg-white border-sage/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-serif text-sage-darker flex items-center gap-2">
            <Heart className="w-5 h-5 text-sage" />
            Motherhood Phases
          </CardTitle>
          <p className="text-sm text-muted-foreground">These appear during user onboarding</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {config.phases.map((phase) => (
            <div 
              key={phase.option_id} 
              className={`p-4 rounded-xl border ${phase.is_active ? 'border-sage/20 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    phase.color === 'sage' ? 'bg-sage/20' :
                    phase.color === 'clay' ? 'bg-clay/20' :
                    phase.color === 'purple' ? 'bg-purple-100' : 'bg-sage/20'
                  }`}>
                    {phase.icon === 'Sparkles' && <Sparkles className="w-5 h-5 text-clay-dark" />}
                    {phase.icon === 'Heart' && <Heart className="w-5 h-5 text-sage-dark" />}
                    {phase.icon === 'Baby' && <Baby className="w-5 h-5 text-purple-600" />}
                    {phase.icon === 'Star' && <Star className="w-5 h-5 text-yellow-600" />}
                    {phase.icon === 'Users' && <Users className="w-5 h-5 text-blue-600" />}
                  </div>
                  <div>
                    <p className="font-medium text-sage-darker">{phase.label}</p>
                    <p className="text-sm text-muted-foreground">{phase.description}</p>
                    <Badge variant="outline" className="mt-1 text-xs border-sage/30">ID: {phase.value}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(phase)}
                    className={phase.is_active ? 'text-green-600' : 'text-gray-400'}
                  >
                    {phase.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(phase.option_id)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Interests Section */}
      <Card className="bg-white border-sage/20">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-serif text-sage-darker flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-sage" />
                User Interests
              </CardTitle>
              <p className="text-sm text-muted-foreground">These appear during user onboarding (drag to reorder)</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReorderMode(!reorderMode)}
              className={reorderMode ? 'bg-sage/10 border-sage' : 'border-sage/30'}
            >
              {reorderMode ? 'Done Reordering' : 'Reorder'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {config.interests.map((interest, index) => (
              <Badge 
                key={interest.option_id} 
                variant={interest.is_active ? "default" : "outline"}
                className={`py-2 px-3 text-sm cursor-pointer group ${
                  interest.is_active 
                    ? 'bg-sage/20 text-sage-darker hover:bg-sage/30' 
                    : 'border-gray-200 text-gray-400'
                }`}
              >
                {reorderMode && (
                  <span className="flex items-center gap-1 mr-2">
                    <button 
                      onClick={() => index > 0 && handleMoveItem('interest', index, index - 1)}
                      disabled={index === 0}
                      className={`text-sage ${index === 0 ? 'opacity-30' : 'hover:text-sage-dark'}`}
                    >
                      ◀
                    </button>
                    <span className="text-xs text-muted-foreground">{index + 1}</span>
                    <button 
                      onClick={() => index < config.interests.length - 1 && handleMoveItem('interest', index, index + 1)}
                      disabled={index === config.interests.length - 1}
                      className={`text-sage ${index === config.interests.length - 1 ? 'opacity-30' : 'hover:text-sage-dark'}`}
                    >
                      ▶
                    </button>
                  </span>
                )}
                {interest.label}
                {!reorderMode && (
                  <>
                    <button 
                      onClick={() => handleToggleActive(interest)}
                      className="ml-2 opacity-60 hover:opacity-100"
                    >
                      {interest.is_active ? '✓' : '○'}
                    </button>
                    <button 
                      onClick={() => handleDelete(interest.option_id)}
                      className="ml-1 text-red-400 opacity-0 group-hover:opacity-100"
                    >
                      ×
                    </button>
                  </>
                )}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Site Content Management
const AdminSiteContent = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [editedValues, setEditedValues] = useState({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/site-content`, { withCredentials: true });
      setContent(response.data);
      // Initialize edited values
      const initial = {};
      Object.values(response.data).flat().forEach(item => {
        initial[item.content_key] = item.value;
      });
      setEditedValues(initial);
    } catch (error) {
      console.error("Content error:", error);
      toast.error("Failed to load site content");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (contentKey) => {
    setSaving(prev => ({ ...prev, [contentKey]: true }));
    try {
      await axios.put(`${API}/admin/site-content/${contentKey}`, 
        { value: editedValues[contentKey] }, 
        { withCredentials: true }
      );
      toast.success("Content updated");
      fetchContent();
    } catch (error) {
      toast.error("Failed to update content");
    } finally {
      setSaving(prev => ({ ...prev, [contentKey]: false }));
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all content to defaults? This cannot be undone.")) return;
    try {
      await axios.post(`${API}/admin/site-content/reset`, {}, { withCredentials: true });
      toast.success("Content reset to defaults");
      fetchContent();
    } catch (error) {
      toast.error("Failed to reset content");
    }
  };

  const hasChanges = (contentKey, originalValue) => {
    return editedValues[contentKey] !== originalValue;
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">
      {[1,2,3].map(i => <div key={i} className="h-32 bg-sage/10 rounded-xl" />)}
    </div>;
  }

  const sectionLabels = {
    landing: "Landing Page",
    global: "Global / Site-wide",
    dashboard: "Dashboard"
  };

  return (
    <div className="space-y-6" data-testid="admin-site-content">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif text-sage-darker">Site Content</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage text and images across the app</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => window.open('/', '_blank')}
            className="border-sage/30 text-sage-dark hover:bg-sage/10"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview Site
          </Button>
          <Button 
            variant="outline"
            onClick={handleReset}
            className="border-red-200 text-red-500 hover:bg-red-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </Button>
        </div>
      </div>

      {Object.entries(content).map(([section, items]) => (
        <Card key={section} className="bg-white border-sage/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-serif text-sage-darker flex items-center gap-2">
              {section === 'global' ? <Image className="w-5 h-5 text-sage" /> : <Type className="w-5 h-5 text-sage" />}
              {sectionLabels[section] || section}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Text Content */}
            {items.filter(item => item.content_type === 'text').length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Type className="w-4 h-4" /> Text Content
                </h3>
                {items.filter(item => item.content_type === 'text').map(item => (
                  <div key={item.content_key} className="space-y-2">
                    <Label className="text-sage-darker text-sm">{item.label}</Label>
                    <div className="flex gap-2">
                      {item.value.length > 100 ? (
                        <Textarea
                          value={editedValues[item.content_key] || ''}
                          onChange={(e) => setEditedValues(prev => ({ ...prev, [item.content_key]: e.target.value }))}
                          className="bg-cream border-sage/20 flex-1"
                          rows={3}
                        />
                      ) : (
                        <Input
                          value={editedValues[item.content_key] || ''}
                          onChange={(e) => setEditedValues(prev => ({ ...prev, [item.content_key]: e.target.value }))}
                          className="bg-cream border-sage/20 flex-1"
                        />
                      )}
                      <Button
                        onClick={() => handleSave(item.content_key)}
                        disabled={!hasChanges(item.content_key, item.value) || saving[item.content_key]}
                        className={`${hasChanges(item.content_key, item.value) ? 'bg-sage text-white hover:bg-sage-dark' : 'bg-gray-100 text-gray-400'}`}
                      >
                        {saving[item.content_key] ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Image Content */}
            {items.filter(item => item.content_type === 'image').length > 0 && (
              <div className="space-y-4 pt-4 border-t border-sage/10">
                <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Image className="w-4 h-4" /> Images
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {items.filter(item => item.content_type === 'image').map(item => (
                    <div key={item.content_key} className="space-y-2">
                      <Label className="text-sage-darker text-sm">{item.label}</Label>
                      <div className="relative rounded-xl overflow-hidden bg-sage/5 aspect-video">
                        <img 
                          src={editedValues[item.content_key] || item.value} 
                          alt={item.label}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Invalid+URL' }}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={editedValues[item.content_key] || ''}
                          onChange={(e) => setEditedValues(prev => ({ ...prev, [item.content_key]: e.target.value }))}
                          className="bg-cream border-sage/20 flex-1 text-xs"
                          placeholder="Enter image URL"
                        />
                        <Button
                          onClick={() => handleSave(item.content_key)}
                          disabled={!hasChanges(item.content_key, item.value) || saving[item.content_key]}
                          className={`${hasChanges(item.content_key, item.value) ? 'bg-sage text-white hover:bg-sage-dark' : 'bg-gray-100 text-gray-400'}`}
                        >
                          {saving[item.content_key] ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Admin Circles
const AdminCircles = () => {
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [circleDetails, setCircleDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // list, details
  const [uploading, setUploading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingCircleId, setEditingCircleId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    phase: "all",
    image_url: ""
  });

  useEffect(() => {
    fetchCircles();
  }, []);

  const fetchCircles = async () => {
    try {
      const response = await axios.get(`${API}/admin/circles`, { withCredentials: true });
      setCircles(response.data);
    } catch (error) {
      console.error("Circles error:", error);
      // Fallback to regular endpoint if admin endpoint fails
      try {
        const fallback = await axios.get(`${API}/circles`, { withCredentials: true });
        setCircles(fallback.data);
      } catch (e) {
        toast.error("Failed to load circles");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCircleDetails = async (circleId) => {
    setDetailsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/circles/${circleId}`, { withCredentials: true });
      setCircleDetails(response.data);
      setViewMode("details");
    } catch (error) {
      toast.error("Failed to load circle details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, PNG, GIF, or WebP)");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      
      const response = await axios.post(`${API}/admin/upload`, uploadData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, image_url: response.data.url }));
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", description: "", phase: "all", image_url: "" });
    setEditMode(false);
    setEditingCircleId(null);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/circles`, formData, { withCredentials: true });
      toast.success("Circle created");
      setDialogOpen(false);
      resetForm();
      fetchCircles();
    } catch (error) {
      toast.error("Failed to create circle");
    }
  };

  const handleEdit = (circle) => {
    setFormData({
      name: circle.name || "",
      description: circle.description || "",
      phase: circle.phase || "all",
      image_url: circle.image_url || ""
    });
    setEditMode(true);
    setEditingCircleId(circle.circle_id);
    setDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/admin/circles/${editingCircleId}`, formData, { withCredentials: true });
      toast.success("Circle updated");
      setDialogOpen(false);
      resetForm();
      fetchCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update circle");
    }
  };

  const handleDelete = async (circleId) => {
    if (!window.confirm("Delete this circle?")) return;
    try {
      await axios.delete(`${API}/admin/circles/${circleId}`, { withCredentials: true });
      toast.success("Circle deleted");
      fetchCircles();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this post and all its comments?")) return;
    try {
      await axios.delete(`${API}/admin/circles/${selectedCircle}/posts/${postId}`, { withCredentials: true });
      toast.success("Post deleted");
      fetchCircleDetails(selectedCircle);
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(`${API}/admin/circles/${selectedCircle}/comments/${commentId}`, { withCredentials: true });
      toast.success("Comment deleted");
      fetchCircleDetails(selectedCircle);
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm("Remove this member from the circle?")) return;
    try {
      await axios.delete(`${API}/admin/circles/${selectedCircle}/members/${userId}`, { withCredentials: true });
      toast.success("Member removed");
      fetchCircleDetails(selectedCircle);
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (viewMode === "details" && circleDetails) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => { setViewMode("list"); setCircleDetails(null); setSelectedCircle(null); }}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Circles
          </Button>
        </div>

        {/* Circle Info */}
        <Card className="bg-sage/5 border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              {circleDetails.circle.image_url && (
                <img src={circleDetails.circle.image_url} alt={circleDetails.circle.name} className="w-20 h-20 object-cover rounded-lg" />
              )}
              <div>
                <h1 className="text-xl font-serif text-sage-darker">{circleDetails.circle.name}</h1>
                <p className="text-muted-foreground text-sm mt-1">{circleDetails.circle.description}</p>
                <div className="flex gap-3 mt-2">
                  <Badge className="bg-sage/20 text-sage-dark">{circleDetails.members?.length || 0} members</Badge>
                  <Badge className="bg-sage/20 text-sage-dark">{circleDetails.posts?.length || 0} posts</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-sage" />
              Members ({circleDetails.members?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-sage/10 border-b border-sage/20 sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-sage-darker">User</th>
                    <th className="text-left p-3 text-sm font-medium text-sage-darker">Email</th>
                    <th className="text-left p-3 text-sm font-medium text-sage-darker">Joined</th>
                    <th className="text-left p-3 text-sm font-medium text-sage-darker">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {circleDetails.members?.map((member) => (
                    <tr key={member.user_id} className="border-b border-sage/10 hover:bg-sage/5">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Avatar className="w-6 h-6">
                            {member.user_picture && <AvatarImage src={member.user_picture} />}
                            <AvatarFallback className="bg-sage/20 text-sage-dark text-xs">{member.user_name?.[0]}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">{member.user_name || "Unknown"}</span>
                        </div>
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">{member.user_email}</td>
                      <td className="p-3 text-sm text-muted-foreground">{formatDate(member.joined_at)}</td>
                      <td className="p-3">
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(member.user_id)} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                          <Trash2 className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Posts Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-sage" />
              Posts ({circleDetails.posts?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {circleDetails.posts?.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No posts in this circle</p>
            ) : (
              circleDetails.posts?.map((post) => (
                <div key={post.post_id} className="p-4 bg-sage/5 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar className="w-6 h-6">
                          {post.author_picture && <AvatarImage src={post.author_picture} />}
                          <AvatarFallback className="bg-sage/20 text-sage-dark text-xs">{post.author_name?.[0]}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-sage-darker">{post.author_name || "Unknown"}</span>
                        <span className="text-xs text-muted-foreground">{post.author_email}</span>
                        {post.posted_anonymously && <Badge className="bg-purple-100 text-purple-700 text-xs">Posted Anonymously</Badge>}
                        <span className="text-xs text-muted-foreground">{formatDate(post.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">{post.content?.substring(0, 300)}{post.content?.length > 300 ? "..." : ""}</p>
                      
                      {/* Comments */}
                      {post.comments && post.comments.length > 0 && (
                        <div className="mt-3 pl-4 border-l-2 border-sage/20 space-y-2">
                          <p className="text-xs font-medium text-muted-foreground mb-2">Comments ({post.comments.length})</p>
                          {post.comments.map((comment) => (
                            <div key={comment.comment_id} className="bg-white p-3 rounded-lg flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Avatar className="w-5 h-5">
                                    {comment.author_picture && <AvatarImage src={comment.author_picture} />}
                                    <AvatarFallback className="bg-sage/20 text-sage-dark text-[10px]">{comment.author_name?.[0]}</AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm font-medium text-sage-darker">{comment.author_name || "Unknown"}</span>
                                  <span className="text-xs text-muted-foreground">{comment.author_email}</span>
                                  {comment.posted_anonymously && <Badge className="bg-purple-100 text-purple-700 text-[10px]">Anon</Badge>}
                                  <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                                </div>
                                <p className="text-xs text-gray-600 pl-7">{comment.content}</p>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteComment(comment.comment_id)} 
                                className="text-red-400 hover:text-red-500 hover:bg-red-50 h-6 w-6 p-0"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                        <span>{post.comment_count || 0} comments</span>
                        <span>{post.likes?.length || 0} likes</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDeletePost(post.post_id)} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-circles">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-serif text-sage-darker">Circle Management</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-sage text-white hover:bg-sage-dark rounded-full" data-testid="create-circle-btn" onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Circle
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-sage/20 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-sage-darker font-serif">{editMode ? "Edit Circle" : "Create Circle"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-sage-darker">Name</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-cream border-sage/20"
                  data-testid="circle-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Description</Label>
                <Textarea 
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-cream border-sage/20"
                  data-testid="circle-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Phase</Label>
                <Select value={formData.phase} onValueChange={(v) => setFormData(prev => ({ ...prev, phase: v }))}>
                  <SelectTrigger className="bg-cream border-sage/20" data-testid="circle-phase-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Phases</SelectItem>
                    <SelectItem value="ttc">Trying to Conceive</SelectItem>
                    <SelectItem value="pregnant">Pregnant</SelectItem>
                    <SelectItem value="postpartum">Postpartum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sage-darker">Circle Image</Label>
                
                {/* Image Preview */}
                {formData.image_url && (
                  <div className="relative w-full h-32 rounded-lg overflow-hidden bg-sage/10 mb-2">
                    <img 
                      src={formData.image_url} 
                      alt="Circle preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Invalid+Image' }}
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 h-6 w-6 p-0"
                      onClick={() => setFormData(prev => ({ ...prev, image_url: "" }))}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                
                {/* Upload Button */}
                <div className="flex gap-2">
                  <label className="flex-1">
                    <div className={`flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-sage/30 rounded-lg cursor-pointer hover:border-sage/50 hover:bg-sage/5 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                      {uploading ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin text-sage" />
                          <span className="text-sm text-sage-dark">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Image className="w-4 h-4 text-sage" />
                          <span className="text-sm text-sage-dark">
                            {formData.image_url ? "Change Image" : "Upload Image"}
                          </span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>
                
                {/* Or use URL */}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>or enter URL:</span>
                </div>
                <Input 
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  className="bg-cream border-sage/20 text-sm"
                  placeholder="https://..."
                  data-testid="circle-image-input"
                />
              </div>
              <Button onClick={editMode ? handleUpdate : handleCreate} disabled={uploading} className="w-full bg-sage text-white hover:bg-sage-dark" data-testid="submit-circle-btn">
                {editMode ? "Update Circle" : "Create Circle"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        {circles.map((circle) => (
          <Card key={circle.circle_id} className="bg-white border-sage/20 overflow-hidden">
            <div className="h-32 bg-sage/10">
              {circle.image_url && <img src={circle.image_url} alt={circle.name} className="w-full h-full object-cover" />}
            </div>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <Badge className={`phase-${circle.phase} mb-2`}>{circle.phase === 'all' ? 'All Phases' : circle.phase}</Badge>
                  <h3 className="text-sage-darker font-medium truncate">{circle.name}</h3>
                  <p className="text-muted-foreground text-sm">{circle.member_count || 0} members • {circle.post_count || 0} posts</p>
                </div>
                <div className="flex gap-1">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { setSelectedCircle(circle.circle_id); fetchCircleDetails(circle.circle_id); }}
                    className="text-sage-dark border-sage/20"
                    title="View details"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleEdit(circle)}
                    className="text-sage-dark"
                    title="Edit circle"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(circle.circle_id)} className="text-red-400" data-testid={`delete-circle-${circle.circle_id}`} title="Delete circle">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Data Export Component
const AdminDataExport = () => {
  const [exporting, setExporting] = useState({});

  const collections = [
    { name: "users", label: "Users", description: "All registered users" },
    { name: "events", label: "Events", description: "All events" },
    { name: "event_registrations", label: "Event Registrations", description: "User registrations for events" },
    { name: "experts", label: "Experts", description: "All experts" },
    { name: "bookings", label: "Session Bookings", description: "Expert session bookings" },
    { name: "time_slots", label: "Time Slots", description: "Expert availability slots" },
    { name: "circles", label: "Circles", description: "Community circles" },
    { name: "posts", label: "Posts", description: "Circle posts" },
    { name: "comments", label: "Comments", description: "Post comments" },
    { name: "connections", label: "Connections", description: "User connections" },
    { name: "messages", label: "Messages", description: "Private messages" },
  ];

  const handleExport = async (collectionName, format) => {
    setExporting(prev => ({ ...prev, [collectionName]: true }));
    try {
      // Use fetch with credentials to get the file
      const response = await fetch(`${API}/admin/export/${collectionName}?format=${format}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': format === 'json' ? 'application/json' : 'text/csv'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        toast.error(`Export failed: ${errorData.detail || response.statusText}`);
        return;
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      if (blob.size === 0) {
        toast.error(`No data to export for ${collectionName}`);
        return;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = `${collectionName}_export.${format}`;
      link.download = filename;
      link.style.display = 'none';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        if (link.parentNode) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 300);
      
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export ${collectionName}: ${error.message || 'Unknown error'}`);
    } finally {
      setExporting(prev => ({ ...prev, [collectionName]: false }));
    }
  };

  const handleExportAll = async () => {
    setExporting(prev => ({ ...prev, all: true }));
    try {
      // Use fetch with credentials to get the file
      const response = await fetch(`${API}/admin/export-all`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        toast.error(`Export failed: ${errorData.detail || response.statusText}`);
        return;
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      if (blob.size === 0) {
        toast.error("No data to export");
        return;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'wombs_full_export.json';
      link.style.display = 'none';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        if (link.parentNode) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 300);
      
      toast.success("Downloaded wombs_full_export.json");
    } catch (error) {
      console.error('Export all error:', error);
      toast.error(`Failed to export database: ${error.message || 'Unknown error'}`);
    } finally {
      setExporting(prev => ({ ...prev, all: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-serif text-sage-darker">Data Export</h1>
        <p className="text-muted-foreground text-sm mt-1">Download database collections in CSV or JSON format</p>
      </div>

      {/* Export All */}
      <Card className="bg-sage/5 border-sage/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-sage-darker">Export All Data</h3>
              <p className="text-sm text-muted-foreground">Download complete database backup as JSON</p>
            </div>
            <Button 
              onClick={handleExportAll} 
              disabled={exporting.all}
              className="bg-sage text-white hover:bg-sage-dark"
            >
              {exporting.all ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Export All (JSON)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Individual Collections */}
      <Card>
        <CardHeader>
          <CardTitle>Individual Collections</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-sage/10">
            {collections.map((coll) => (
              <div key={coll.name} className="flex items-center justify-between p-4 hover:bg-sage/5">
                <div>
                  <h4 className="font-medium text-sage-darker">{coll.label}</h4>
                  <p className="text-xs text-muted-foreground">{coll.description}</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleExport(coll.name, 'csv')}
                    disabled={exporting[coll.name]}
                    className="text-sage-dark border-sage/20"
                  >
                    {exporting[coll.name] ? <RefreshCw className="w-3 h-3 animate-spin" /> : "CSV"}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleExport(coll.name, 'json')}
                    disabled={exporting[coll.name]}
                    className="text-sage-dark border-sage/20"
                  >
                    {exporting[coll.name] ? <RefreshCw className="w-3 h-3 animate-spin" /> : "JSON"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Admin Dashboard
// Admin Dashboard CMS - Manage post-login dashboard content
const AdminDashboardCMS = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [editedValues, setEditedValues] = useState({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/site-content?section=dashboard`);
      setContent(response.data);
      // Initialize edited values
      const initial = {};
      Object.values(response.data).forEach(item => {
        initial[item.content_key] = item.value;
      });
      setEditedValues(initial);
    } catch (error) {
      console.error("Content error:", error);
      toast.error("Failed to load dashboard content");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (contentKey) => {
    setSaving(prev => ({ ...prev, [contentKey]: true }));
    try {
      await axios.put(`${API}/admin/site-content/${contentKey}`, 
        { value: editedValues[contentKey] }, 
        { withCredentials: true }
      );
      toast.success("Content updated");
      fetchContent();
    } catch (error) {
      toast.error("Failed to update content");
    } finally {
      setSaving(prev => ({ ...prev, [contentKey]: false }));
    }
  };

  const hasChanges = (contentKey, originalValue) => {
    return editedValues[contentKey] !== originalValue;
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">
      {[1,2,3].map(i => <div key={i} className="h-24 bg-sage/10 rounded-xl" />)}
    </div>;
  }

  const contentItems = Object.values(content);

  return (
    <div className="space-y-6" data-testid="admin-dashboard-cms">
      <div>
        <h1 className="text-2xl font-serif text-sage-darker">Dashboard Content Management</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Customize the text that appears on the user dashboard (post-login homepage)
        </p>
      </div>

      <div className="grid gap-4">
        {contentItems.map((item) => (
          <Card key={item.content_key} className="bg-white border-sage/20">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Label className="text-sage-darker font-medium">
                    {item.label}
                  </Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Key: {item.content_key}
                  </p>
                  <Input
                    value={editedValues[item.content_key] || ""}
                    onChange={(e) => setEditedValues(prev => ({
                      ...prev,
                      [item.content_key]: e.target.value
                    }))}
                    className="bg-cream border-sage/20"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={() => handleSave(item.content_key)}
                  disabled={!hasChanges(item.content_key, item.value) || saving[item.content_key]}
                  className={`${
                    hasChanges(item.content_key, item.value)
                      ? 'bg-sage text-white hover:bg-sage-dark'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {saving[item.content_key] ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {contentItems.length === 0 && (
        <Card className="bg-cream border-sage/20">
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-sage/40 mx-auto mb-2" />
            <p className="text-muted-foreground">No dashboard content configured</p>
            <p className="text-sm text-muted-foreground">
              Dashboard content will be automatically created on first access
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  return (
    <div className="min-h-screen bg-cream">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <Routes>
          <Route path="/" element={<AdminOverview />} />
          <Route path="/site-content" element={<AdminSiteContent />} />
          <Route path="/dashboard-cms" element={<AdminDashboardCMS />} />
          <Route path="/app-settings" element={<AdminAppSettings />} />
          <Route path="/calendar-settings" element={<AdminCalendarSettings />} />
          <Route path="/users" element={<AdminUsers />} />
          <Route path="/experts" element={<AdminExperts />} />
          <Route path="/bookings" element={<AdminBookings />} />
          <Route path="/events" element={<AdminEvents />} />
          <Route path="/events/:eventId/registrations" element={<AdminEventRegistrations />} />
          <Route path="/circles" element={<AdminCircles />} />
          <Route path="/data-export" element={<AdminDataExport />} />
        </Routes>
      </div>
    </div>
  );
};

export default AdminDashboard;
